import { useEffect, useState } from "react";
import { Loader2, FlaskConical, Smartphone, CreditCard, Landmark, Timer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, fmtDate } from "@/lib/api";

const METHODS = [
  { id: "upi", icon: Smartphone, label: "UPI · GPay / PhonePe / Paytm" },
  { id: "card", icon: CreditCard, label: "Credit / Debit Card" },
  { id: "netbanking", icon: Landmark, label: "Netbanking" },
];

const Countdown = ({ until }) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(until) - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [until]);
  return <span data-testid="lock-countdown">{Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span>;
};

export const SandboxPayDialog = ({ booking, onClose, onRelease, onPaid, onExpired }) => {
  const [method, setMethod] = useState("upi");
  const [busy, setBusy] = useState(false);

  const close = () => { if (booking) onRelease(booking); onClose(); };
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/sandbox-confirm`, { booking_ref: booking.booking_ref });
      onClose();
      onPaid(data);
    } catch (e) {
      onClose();
      onExpired(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => { if (!o && !busy) close(); }}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="sandbox-pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Complete payment</DialogTitle>
          <DialogDescription>Smileton Dental · Ref {booking?.booking_ref}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 flex gap-2" data-testid="sandbox-warning">
          <FlaskConical className="h-4 w-4 shrink-0" /> Demo payment mode — no real money moves. Live Razorpay checkout opens here once the clinic switches to live payments.
        </div>
        {booking && (
          <div className="rounded-2xl border border-slate-200 p-4 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-ink-muted">Consultation</span><span className="font-display text-xl font-semibold text-teal" data-testid="sandbox-amount">₹{booking.fee}</span></div>
            <p className="text-ink">{booking.visit_reason_title}</p>
            <p className="text-ink-muted">{fmtDate(booking.appointment_date)} · {booking.slot_label}</p>
            <p className="text-xs text-ink-muted flex items-center gap-1 pt-1"><Timer className="h-3.5 w-3.5" /> Slot held for <Countdown until={booking.lock_expires_at} /></p>
          </div>
        )}
        <div className="space-y-2">
          {METHODS.map(({ id, icon: I, label }) => (
            <button key={id} type="button" onClick={() => setMethod(id)} data-testid={`sandbox-method-${id}`}
              className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-3 text-sm text-left transition-colors ${method === id ? "border-teal bg-mint" : "border-slate-200 hover:bg-slate-50"}`}>
              <I className="h-4 w-4 text-teal" />{label}
            </button>
          ))}
        </div>
        <button type="button" onClick={pay} disabled={busy} data-testid="sandbox-pay-button"
          className="w-full h-12 rounded-full bg-teal text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-teal-dark disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Pay ₹{booking?.fee} (Demo)
        </button>
        <button type="button" onClick={close} disabled={busy} className="w-full text-sm text-ink-muted hover:text-red-600" data-testid="sandbox-cancel-button">
          Cancel &amp; release slot
        </button>
      </DialogContent>
    </Dialog>
  );
};
